import {useGlobalAddressContext} from '@/app/signup/(state)/GlobalAddressContext';
import {useEffect, useMemo} from 'react';
import {useSnapshot} from 'valtio';
import type {INTERNAL_Snapshot as Snapshot} from 'valtio/vanilla';
import {
  type Step,
  type StepWidgetStore,
  StepsPath,
  stepsList,
} from '../StepsRouting';
import {useLoadCurrentStep} from './useLoadStep';
import {useSignupRedirect} from './useSignupRedirect';

/**
 * Wrapper around useSnapshot hook with proper redirect behaivor for widget store
 * During development this hook will be called twice,
 * it caused by fact that React tests each component through sequence: mount -> unmount -> mount
 * But it should work properly on production.
 * More info:https://react.dev/learn/synchronizing-with-effects#how-to-handle-the-effect-firing-twice-in-development
 * */
export const useWidget = <T extends StepWidgetStore = StepWidgetStore>(
  page: StepsPath,
  widget: T,
): Snapshot<T> => {
  const {updatedGlobalAddressId} = useGlobalAddressContext();
  const [step] = useGetStepByPath(page);
  const loadCurrentStep = useLoadCurrentStep(step);
  const signupRedirect = useSignupRedirect();

  const snapshot = useSnapshot(widget) || {};

  useEffect(() => {
    // handle redirection
    if (signupRedirect()) return;

    // don't need to load data if there is no global address id
    if (!updatedGlobalAddressId) return;

    if (snapshot.content) {
      // TODO: verify is still valid for certain edge case
      /*
       * edge case example:
         go to a step (reivew)  ->
         back to a previous step (phone) ->
         update some production seletion ->
         instead of clicking next button, manually go back to the last step by manually input the url in the browser.
       */
      // returned snapshot is good, nothing more need
      return;
    }

    // user opened page where he was
    // need load current state of the app
    loadCurrentStep();
  }, [
    loadCurrentStep,
    signupRedirect,
    snapshot.content,
    updatedGlobalAddressId,
  ]); // should run only once per mount

  return snapshot;
};

export const useGetStepByPath = (
  pathname?: string,
): [Step, number, boolean] => {
  const stepIndex = useMemo(() => {
    const index = stepsList.findIndex(({path}) => path === pathname);
    return index === -1 ? 0 : index; // fallback to first step by default
  }, [pathname]);
  const step = stepsList[stepIndex];
  const isLastStep = (step.path as StepsPath) === StepsPath.EndBuyflow;

  return [step, stepIndex, isLastStep];
};
